import { useState, useMemo } from 'react';
import { useSelectedStore } from '@/contexts/StoreContext';
import { useStore, usePagination } from '@/hooks';
import {
  useWeeklyOperatingHours,
  useCreateWeeklyOperatingHours,
  useUpdateWeeklyOperatingHours,
  useDeleteWeeklyOperatingHours,
} from '@/hooks/useWeeklyOperatingHours';
import {
  useSpecialDateOperatingHoursPaginated,
  useCreateSpecialDateOperatingHours,
  useUpdateSpecialDateOperatingHours,
  useDeleteSpecialDateOperatingHours,
} from '@/hooks/useSpecialDateOperatingHours';
import type { WeeklyOperatingHours, SpecialDateOperatingHours } from '@geely/shared/types';
import type {
  WeeklyOperatingHoursFormData,
  SpecialDateOperatingHoursFormData
} from '@geely/shared/schemas';
import { getAvailableDates, extractTimeFields } from '@/lib/utils';
import { toast } from 'sonner';

export function useOperatingHoursPageData() {
  const { selectedStoreId } = useSelectedStore();
  const userStoreId = selectedStoreId ?? undefined;

  // Stores
  const { data: userStore } = useStore(userStoreId ?? '');

  // Weekly Operating Hours
  const {
    data: weeklyHours = [],
    isLoading: isLoadingWeekly
  } = useWeeklyOperatingHours(userStoreId);

  const createWeeklyMutation = useCreateWeeklyOperatingHours();
  const updateWeeklyMutation = useUpdateWeeklyOperatingHours();
  const deleteWeeklyMutation = useDeleteWeeklyOperatingHours();

  const [isWeeklyDialogOpen, setIsWeeklyDialogOpen] = useState(false);
  const [editingWeekly, setEditingWeekly] = useState<WeeklyOperatingHours | null>(null);

  // Special Date Operating Hours (paginated)
  const { page: specialDatePage, limit: specialDateLimit, setPage: setSpecialDatePage } = usePagination();
  const {
    data: paginatedSpecialDates,
    isLoading: isLoadingSpecialDates
  } = useSpecialDateOperatingHoursPaginated(userStoreId, { page: specialDatePage, limit: specialDateLimit });
  const specialDateHours = paginatedSpecialDates?.data ?? [];
  const specialDatePaginationMeta = paginatedSpecialDates?.meta;

  const createSpecialDateMutation = useCreateSpecialDateOperatingHours();
  const updateSpecialDateMutation = useUpdateSpecialDateOperatingHours();
  const deleteSpecialDateMutation = useDeleteSpecialDateOperatingHours();

  const [isSpecialDateDialogOpen, setIsSpecialDateDialogOpen] = useState(false);
  const [editingSpecialDate, setEditingSpecialDate] = useState<SpecialDateOperatingHours | null>(null);

  // Available dates for special dates (60 days ahead)
  const availableDates = getAvailableDates();

  const usedDates = useMemo(() => {
    return new Set(
      specialDateHours
        .filter((h) => h.storeId === userStoreId)
        .map((h) => h.date)
    );
  }, [specialDateHours, userStoreId]);

  const availableDatesForForm = useMemo(() => {
    return availableDates.filter(
      (date) => !usedDates.has(date) || date === editingSpecialDate?.date
    );
  }, [availableDates, usedDates, editingSpecialDate]);

  // Weekly Hours handlers
  const handleOpenWeeklyDialog = (hours?: WeeklyOperatingHours) => {
    setEditingWeekly(hours || null);
    setIsWeeklyDialogOpen(true);
  };

  const handleCloseWeeklyDialog = () => {
    setIsWeeklyDialogOpen(false);
    setEditingWeekly(null);
  };

  const handleSubmitWeekly = async (data: WeeklyOperatingHoursFormData) => {
    try {
      const timeFields = extractTimeFields(data);

      if (editingWeekly) {
        await updateWeeklyMutation.mutateAsync({
          id: editingWeekly.id,
          storeId: userStoreId!,
          data: {
            isClosed: data.isClosed,
            hasLunchBreak: data.hasLunchBreak,
            parallelAppointments: data.parallelAppointments,
            isActive: data.isActive,
            ...timeFields,
          },
        });
        toast.success('Horário semanal atualizado com sucesso!');
      } else {
        await createWeeklyMutation.mutateAsync({
          storeId: userStoreId!,
          dayOfWeek: data.dayOfWeek,
          isClosed: data.isClosed,
          hasLunchBreak: data.hasLunchBreak,
          parallelAppointments: data.parallelAppointments,
          isActive: data.isActive,
          ...timeFields,
        });
        toast.success('Horário semanal criado com sucesso!');
      }
      handleCloseWeeklyDialog();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao salvar horário semanal';
      toast.error(errorMessage);
    }
  };

  const handleDeleteWeekly = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este horário semanal?')) {
      try {
        await deleteWeeklyMutation.mutateAsync({ id, storeId: userStoreId! });
        toast.success('Horário semanal excluído com sucesso!');
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao excluir horário semanal';
        toast.error(errorMessage);
      }
    }
  };

  // Special Date Hours handlers
  const handleOpenSpecialDateDialog = (hours?: SpecialDateOperatingHours) => {
    setEditingSpecialDate(hours || null);
    setIsSpecialDateDialogOpen(true);
  };

  const handleCloseSpecialDateDialog = () => {
    setIsSpecialDateDialogOpen(false);
    setEditingSpecialDate(null);
  };

  const handleSubmitSpecialDate = async (data: SpecialDateOperatingHoursFormData) => {
    try {
      const timeFields = extractTimeFields(data);

      if (editingSpecialDate) {
        await updateSpecialDateMutation.mutateAsync({
          id: editingSpecialDate.id,
          storeId: userStoreId!,
          data: {
            isClosed: data.isClosed,
            hasLunchBreak: data.hasLunchBreak,
            parallelAppointments: data.parallelAppointments,
            isActive: data.isActive,
            isGeelyCareWeekend: data.isGeelyCareWeekend,
            ...timeFields,
          },
        });
        toast.success('Data especial atualizada com sucesso!');
      } else {
        await createSpecialDateMutation.mutateAsync({
          storeId: userStoreId!,
          date: data.date,
          isClosed: data.isClosed,
          hasLunchBreak: data.hasLunchBreak,
          parallelAppointments: data.parallelAppointments,
          isActive: data.isActive,
          isGeelyCareWeekend: data.isGeelyCareWeekend,
          ...timeFields,
        });
        toast.success('Data especial criada com sucesso!');
      }
      handleCloseSpecialDateDialog();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao salvar data especial';
      toast.error(errorMessage);
    }
  };

  const handleDeleteSpecialDate = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta data especial?')) {
      try {
        await deleteSpecialDateMutation.mutateAsync({ id, storeId: userStoreId! });
        toast.success('Data especial excluída com sucesso!');
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao excluir data especial';
        toast.error(errorMessage);
      }
    }
  };

  return {
    // Store
    userStore,

    // Weekly Hours
    weeklyHours,
    isLoadingWeekly,
    isWeeklyDialogOpen,
    editingWeekly,
    handleOpenWeeklyDialog,
    handleCloseWeeklyDialog,
    handleSubmitWeekly,
    handleDeleteWeekly,

    // Special Date Hours
    specialDateHours,
    isLoadingSpecialDates,
    specialDatePaginationMeta,
    setSpecialDatePage,
    availableDatesForForm,
    isSpecialDateDialogOpen,
    editingSpecialDate,
    handleOpenSpecialDateDialog,
    handleCloseSpecialDateDialog,
    handleSubmitSpecialDate,
    handleDeleteSpecialDate,
  };
}
